// src/providers/QueryProvider.jsx
import React from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { logger } from '../utils/logger';

/**
 * Single app-wide QueryClient with sensible defaults
 * (tuned for sports-y data that benefits from periodic background refresh).
 */
export const gameQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data considered fresh for 5 minutes, kept in cache for 10 minutes
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000, // v5: gcTime replaces cacheTime
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: false,
      refetchInterval: 5 * 60 * 1000,
      refetchIntervalInBackground: false,
    },
    mutations: {
      retry: 1,
      onError: (error) => {
        logger.error('🔥 Mutation Error:', error);
      },
    },
  },
});

/**
 * Optional mutation defaults for a logical key.
 * NOTE: Provide a real mutationFn in callers or override this key in-place.
 */
gameQueryClient.setMutationDefaults(['submitAnalysis'], {
  mutationFn: async () => {
    // No-op default so we never throw if a screen forgot to wire its fn.
    logger.warn('submitAnalysis mutation called without an implementation.');
    return { ok: false };
  },
  onError: (error) => {
    logger.error('Analysis submission failed:', error);
  },
  onSuccess: () => {
    logger.success('✅ Analysis submitted successfully');
    gameQueryClient.invalidateQueries({ queryKey: ['analyses'] });
    gameQueryClient.invalidateQueries({ queryKey: ['userStats'] });
  },
});

/**
 * Provider
 */
export const QueryProvider = ({ children }) => {
  return (
    <QueryClientProvider client={gameQueryClient}>
      {children}
      {/* Only show devtools in dev builds */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom-right"
          toggleButtonProps={{
            style: {
              backgroundColor: '#1f2937',
              color: '#f3f4f6',
              border: '1px solid #374151',
            },
          }}
        />
      )}
    </QueryClientProvider>
  );
};

export default QueryProvider;

/* -----------------------------------------------------------------------------
   Query utilities (v5 API)
----------------------------------------------------------------------------- */

/** Invalidate a handful of game-related queries. */
export const invalidateGameQueries = (teamId, season) => {
  const keys = [
    ['schedule', teamId, season],
    ['gameData', teamId],
    ['predictions', teamId, season],
    ['playerStats', teamId, season],
  ];
  keys.forEach((queryKey) => {
    gameQueryClient.invalidateQueries({ queryKey });
  });
};

/** Prefetch a team's schedule (safe placeholder queryFn below). */
export const prefetchSchedule = async (teamId, season) => {
  await gameQueryClient.prefetchQuery({
    queryKey: ['schedule', teamId, season],
    queryFn: () => fetchScheduleData(teamId, season),
    staleTime: 10 * 60 * 1000,
  });
};

/** Warm a small set of commonly-used caches for a game screen. */
export const warmGameCache = async (gameData) => {
  const { teamId, season, league } = gameData || {};
  if (!teamId || !season) {
    logger.warn('warmGameCache called without teamId/season.');
    return;
  }

  const tasks = [
    gameQueryClient.prefetchQuery({
      queryKey: ['schedule', teamId, season],
      queryFn: () => fetchScheduleData(teamId, season),
    }),
    gameQueryClient.prefetchQuery({
      queryKey: ['teamStats', teamId, season],
      queryFn: () => fetchTeamStats(teamId, season),
    }),
    gameQueryClient.prefetchQuery({
      queryKey: ['leagueStandings', league, season],
      queryFn: () => fetchLeagueStandings(league, season),
    }),
  ];

  await Promise.allSettled(tasks);
  logger.firebase('🔥 Game cache warmed successfully');
};

/* -----------------------------------------------------------------------------
   Convenience hooks (non-throwing; return stable shapes)
   These can be used right away without wiring services yet.
----------------------------------------------------------------------------- */

export const useScheduleQuery = (teamId, season, options = {}) => {
  const enabled = Boolean(teamId && season) && (options.enabled ?? true);
  return useQuery({
    queryKey: ['schedule', teamId, season],
    queryFn: () => fetchScheduleData(teamId, season),
    enabled,
    select: (data) => data ?? [], // always an array
    ...options,
  });
};

export const useTeamStatsQuery = (teamId, season, options = {}) => {
  const enabled = Boolean(teamId && season) && (options.enabled ?? true);
  return useQuery({
    queryKey: ['teamStats', teamId, season],
    queryFn: () => fetchTeamStats(teamId, season),
    enabled,
    select: (data) => data ?? { teamId, season, stats: {} },
    ...options,
  });
};

export const useLeagueStandingsQuery = (league, season, options = {}) => {
  const enabled = Boolean(league && season) && (options.enabled ?? true);
  return useQuery({
    queryKey: ['leagueStandings', league, season],
    queryFn: () => fetchLeagueStandings(league, season),
    enabled,
    select: (data) => data ?? { league, season, standings: [] },
    ...options,
  });
};

/* -----------------------------------------------------------------------------
   Safe, non-throwing placeholders.
   Replace these with your SportsData service when ready.
   They return stable shapes so your UI won’t crash.
----------------------------------------------------------------------------- */

const fetchScheduleData = async (teamId, season) => {
  logger.debug('fetchScheduleData (placeholder):', { teamId, season });
  // Example stable structure; keep consistent with your consumers.
  return [];
};

const fetchTeamStats = async (teamId, season) => {
  logger.debug('fetchTeamStats (placeholder):', { teamId, season });
  return { teamId, season, stats: {} };
};

const fetchLeagueStandings = async (league, season) => {
  logger.debug('fetchLeagueStandings (placeholder):', { league, season });
  return { league, season, standings: [] };
};
